import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';


interface FaqItem {
  question: string;
  answer: string;
}

interface FaqSection {
  title: string;
  items: FaqItem[];
}

const faqData: FaqSection[] = [
  {
    title: "About Tarot",
    items: []
  },
  {
    title: "",
    items: [
      {
        question: 'How does the app work?',
        answer: "**If you have a physical deck:**\nYou pull your own card(s) and enter your question, spread, and reflections into a short form.\n\nTara (that's the app!) uses AI to interpret your cards through the lens of traditional tarot meanings, your notes, and the context of your spread.\n\nYou receive a layered, emotionally intelligent reading with practical insights and reflection prompts — all personalized to you.\n\n**No deck? No problem:**\nYou can use the Virtual Deck Experience to draw cards randomly from a digital Rider-Waite deck. The app will walk you through your reading just like the physical deck flow — card by card, insight by insight.\n\nSaved readings are stored privately in your journal, so you can revisit them any time.\n\n✨ Whether you're brand new to tarot or deep in your practice, this app is here to support your personal insight journey — not predict your future."
      },
      {
        question: 'How Are Interpretations Generated?',
        answer: 'Tara\'s Tarot blends ancient symbolism with modern insight.\nWhen you ask a question and select your cards, the app draws on:\n\n- Traditional meanings from the Rider-Waite-Smith tarot system\n- The context of your spread and your own card reflections\n- Intuitive themes that emerge from thousands of collective readings\n\n✨ Think of Tara as your digital tarot mentor — someone who helps connect the dots between your intuition, your question, and centuries of symbolic wisdom.\n\nTara doesn\'t replace your voice. She reflects it back — layered with clarity, curiosity, and care.'
      },
      {
        question: 'Do I need to know how to read tarot to use this app?',
        answer: "Not at all. Tara's Tarot is designed for beginners, seasoned readers, and everyone in between.\nYou don't need to memorize meanings or understand spreads to get value from the app. All you need is curiosity and a willingness to reflect.\n\nIf you're just starting out, the app gently guides you through a one-card reading with prompts to help you form your question, reflect on the card, and receive an interpretation that blends traditional meaning with your insights.\n\nYou'll also have access to a full suite of learning tools, including:\n\n**Tarot 101**: an overview of how tarot works, the history, and what to expect\n\n**The Tarot Companion**: a searchable library of card meanings, rituals, and reading tips\n\n**Spread Guides**: visual walkthroughs of how to lay out and interpret common spreads\n\n**The Virtual Deck Viewer**: explore all 78 cards, filter by arcana or suit, and study each card in depth\n\nYou're not expected to know everything. This app is here to help you learn and grow.\n\nTarot is a personal, evolving practice, and this space is built to support your journey at every step."
      },
      {
        question: 'Is my data private?',
        answer: "Yes. Your readings and notes are tied to your personal account and are stored securely. Only you can access your journal entries once you're logged in.\n\nWe never use your entries to train AI models, and your data will never be shared or sold. Tara's interpretations are generated in real time based only on what you share in the moment.\n\nYou're in full control — and you can always edit or delete any of your saved readings."
      },
      {
        question: 'Can I use any tarot deck?',
        answer: 'Absolutely. While the app references the Rider-Waite system for imagery and interpretation, it welcomes all decks and traditions.'
      },
      {
        question: 'Who is Tara?',
        answer: 'Hi, I\'m Tara and the creator of this app! To learn more about me, visit <Link to="/about">About Tara</Link>.'
      }
    ]
  }
];

const FaqPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [openItems, setOpenItems] = useState<string[]>([]);

  const toggleItem = (sectionTitle: string, question: string) => {
    const itemId = `${sectionTitle}-${question}`;
    setOpenItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [itemId]
    );
  };

  const isItemOpen = (sectionTitle: string, question: string) => {
    return openItems.includes(`${sectionTitle}-${question}`);
  };

  const renderAnswer = (answer: string) => {
    // Replace <Link> tags with actual Link components
    const parts = answer.split(/(<Link[^>]*>.*?<\/Link>)/);
    return parts.map((part, index) => {
      if (part.startsWith('<Link')) {
        const to = part.match(/to="([^"]*)">/)?.[1];
        const text = part.match(/>([^<]*)</)?.[1];
        if (to && text) {
          return (
            <Link
              key={index}
              to={to}
              className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
            >
              {text}
            </Link>
          );
        }
      }
      // Handle bold text and line breaks
      return part.split('\n').map((line, i) => {
        // Handle bold text wrapped in **
        const boldParts = line.split(/(\*\*.*?\*\*)/g);
        return (
          <React.Fragment key={`${index}-${i}`}>
            {boldParts.map((boldPart, j) => {
              if (boldPart.startsWith('**') && boldPart.endsWith('**')) {
                return <strong key={j}>{boldPart.slice(2, -2)}</strong>;
              }
              return boldPart;
            })}
            {i < part.split('\n').length - 1 && <br />}
          </React.Fragment>
        );
      });
    });
  };

  return (
    <div className="min-h-[calc(100vh-8rem)] bg-parchment dark:bg-ink">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="max-w-3xl mx-auto"
      >
        <div className="bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl p-8">
          <h1 className="text-3xl md:text-4xl font-display font-semibold text-ink dark:text-parchment mb-8">
            About This Application
          </h1>

          <div className="space-y-8">
            {faqData.map((section) => (
              <div key={section.title}>
                {section.items.length > 0 && (
                  <div className="space-y-4">
                    {section.items.map((item) => (
                      <div
                        key={item.question}
                        className="border border-mist dark:border-dusk rounded-lg overflow-hidden"
                      >
                        <button
                          onClick={() => toggleItem(section.title, item.question)}
                          className="w-full px-4 py-3 flex items-center justify-between bg-parchment/60 dark:bg-shadow/60 hover:bg-parchment dark:hover:bg-shadow transition-colors text-left"
                        >
                          <span className="font-medium text-ink dark:text-parchment">
                            {item.question}
                          </span>
                          <span className="flex-shrink-0 text-ink/50 text-lg">{isItemOpen(section.title, item.question) ? '−' : '+'}</span>
                        </button>
                        {isItemOpen(section.title, item.question) && (
                          <div className="px-4 py-3 bg-parchment/60 dark:bg-shadow/60 border-t border-mist dark:border-dusk">
                            <p className="text-ink dark:text-parchment/60">
                              {renderAnswer(item.answer)}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default FaqPage;
